import { isValidCoordinate } from "geolib";

import { LIVE_MAP_DEFER_MAP_RENDER_MIN_SELECTED } from "../constants/map-bulk-autofit.const";
import type {
  ICoordinate,
  ILatitudeLongitude,
  ILocationInfo,
  IRideRoute,
} from "../models";

type StationLocationBase = {
  locationInfo?: ILocationInfo | null;
};

const isValidMapAxisValue = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value !== 0;

/** Lat/lng in geolib range. Missing values and 0 are invalid (0 often means unset data). */
export const isValidCoordinates = <T extends Partial<ILatitudeLongitude>>(
  location?: T | null,
): location is T & ILatitudeLongitude => {
  const { latitude, longitude } = location ?? {};
  if (!isValidMapAxisValue(latitude) || !isValidMapAxisValue(longitude)) {
    return false;
  }
  return isValidCoordinate({ lat: latitude, lng: longitude });
};

/** `ILocationInfo` with finite lat/lng that geolib accepts for map use. */
export const isLocationInfoWithValidMapCoordinates = (
  locationInfo: ILocationInfo | null | undefined,
): locationInfo is ILocationInfo & ILatitudeLongitude =>
  isValidCoordinates(locationInfo);

/** Station (or similar) whose `locationInfo` is drawable on a map. */
export const isStationWithValidMapCoordinates = <T extends StationLocationBase>(
  station: T | null | undefined,
): station is T & {
  locationInfo: ILocationInfo & { latitude: number; longitude: number };
} => isLocationInfoWithValidMapCoordinates(station?.locationInfo);

/** Planned/tracking route that can be drawn on the live map. */
export const isLiveMapRenderableRideRoute = (route: IRideRoute): boolean =>
  Boolean(route.trackingRenderState?.pointCount) ||
  Boolean(route.routePolyline) ||
  Boolean(route.locations?.some(isLocationInfoWithValidMapCoordinates));

export const shouldDeferLiveMapMapRender = ({
  currentSelectedCount,
  nextSelectedCount,
}: {
  currentSelectedCount: number;
  nextSelectedCount?: number;
}): boolean => {
  const peak = Math.max(
    currentSelectedCount,
    nextSelectedCount ?? currentSelectedCount,
  );

  return peak >= LIVE_MAP_DEFER_MAP_RENDER_MIN_SELECTED;
};

/**
 * Whether two coordinates name the same point. Exact comparison on purpose: both sides come from the same
 * stored value, so this matches a marker back to the popup it opened rather than testing nearness.
 */
export const isSameCoordinate = (
  a: ICoordinate | null | undefined,
  b: ICoordinate | null | undefined,
): boolean => Boolean(a && a.lat === b?.lat && a.lng === b?.lng);
